/**
 * The MAHI typographic mark — a wide geometric sans wordmark drawn as filled
 * outlines rather than set in a font, so the one detail that makes it "MAHI"
 * and not a generic sans render (the A as a bare inverted-V, no crossbar) is
 * exact and doesn't depend on which typeface a browser/OS substitutes.
 *
 * Each letter is a single filled polygon rather than a stroked centerline:
 * an SVG stroke's miter joins spike past the nominal vertex at a sharp
 * corner (the M's two peaks, the A's apex — see git history on this file
 * for the ~15-20 unit overshoot that produced), which threw off both the
 * cap height (M/A read taller than H/I) and the top alignment across
 * letters. These outlines were solved letter-by-letter (offset-line
 * intersection, not eyeballed) so every letter's ink is exactly flush
 * top and bottom — verified by rendering the mark against top/bottom
 * guide lines in a real browser before landing this.
 *
 * fill="currentColor", so sizing is just a height utility on the caller
 * (`h-6 w-auto`, etc.) and color follows whatever text color class wraps
 * it — the brand default is a dark charcoal/slate (e.g. `text-slate-700`),
 * but pages that already tint their MAHI mentions with a brand accent
 * (raspberry, etc.) keep doing that by wrapping this in their existing
 * color class.
 *
 * The geometry is exported (MAHI_VIEWBOX / MAHI_GLYPHS) rather than kept
 * private because the session-entry animation (components/ui/MahiIntro)
 * has to move the four letters independently, which this component — one
 * static mark — deliberately cannot express. Sharing the path data means
 * the animated wordmark and the masthead can never drift apart. The one
 * copy that does have to be hand-synced is the pre-JS boot mark inlined in
 * public/index.html, which by definition cannot import anything.
 */
import React from 'react';

/** Viewport of the wordmark, ink flush at x 0..369.706, y 0..100. */
export const MAHI_VIEWBOX = '-10 -10 389.706 120';

/**
 * The four letters, in reading order. `transform` places each glyph on the
 * baseline grid; `center` is the x of the glyph's ink centre in viewBox units,
 * which is what any per-letter choreography needs to reason about spacing.
 */
export const MAHI_GLYPHS = [
  { id: 'M', transform: 'translate(7,0)',       center: 50,      d: 'M7,100 L7,31.684 L43,64.468 L79,31.684 L79,100 L93,100 L93,0 L43,45.532 L-7,0 L-7,100 Z' },
  // A — bare apex, no crossbar
  { id: 'A', transform: 'translate(135.853,0)', center: 178.853, d: 'M7.853,100 L43,30.886 L78.147,100 L93.853,100 L43,0 L-7.853,100 Z' },
  { id: 'H', transform: 'translate(257.706,0)', center: 292.706, d: 'M0,0 L14,0 L14,100 L0,100 Z M56,0 L70,0 L70,100 L56,100 Z M0,43 L70,43 L70,57 L0,57 Z' },
  { id: 'I', transform: 'translate(355.706,0)', center: 362.706, d: 'M0,0 L14,0 L14,100 L0,100 Z' },
];

export default function MahiLogo({ className = '', title = 'MAHI', decorative = false, ...props }) {
  return (
    <svg
      viewBox={MAHI_VIEWBOX}
      className={className}
      fill="currentColor"
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative ? 'true' : undefined}
      aria-label={decorative ? undefined : title}
      {...props}
    >
      {MAHI_GLYPHS.map((glyph) => (
        <path key={glyph.id} transform={glyph.transform} d={glyph.d} />
      ))}
    </svg>
  );
}
